import {useState, useCallback, useMemo, useEffect} from 'react';

import {ProjectRequester} from '../../../MobxStores/RequestApi/ProjectRequester';

const hasDiffs = (c) => c.selectAll !== undefined || c.added?.size > 0 || c.removed?.size > 0;

const serializeChange = ([role, c]) => ({
    role,
    selectAll: c.selectAll,
    added: c.added ? [...c.added] : [],
    removed: c.removed ? [...c.removed] : [],
});

const buildChangesList = (changes) =>
    Object.entries(changes).filter(([, c]) => hasDiffs(c)).map(serializeChange);

const computePermittedCount = (summary, role, c) => {
    const total = summary.totalItems;
    if (!c) return {permitted: summary.roleCounts[role] || 0, total};
    if (c.selectAll === true) return {permitted: total - (c.removed?.size || 0), total};
    if (c.selectAll === false) return {permitted: c.added?.size || 0, total};
    const base = summary.roleCounts[role] || 0;
    return {permitted: base + (c.added?.size || 0) - (c.removed?.size || 0), total};
};

const useRoleAccessRules = (projectId, projectName, workspace, refreshKey) => {
    const [summary, setSummary] = useState(null);
    const [changes, setChanges] = useState({});

    const loadSummary = useCallback(async () => {
        if (!projectId || !workspace) return;
        try {
            const data = await ProjectRequester.getRoleVisibilitySummary(projectId, workspace);
            setSummary(data);
        } catch (error) {
            console.error('Failed to load role visibility summary:', error);
        }
    }, [projectId, workspace]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary, refreshKey]);

    const updateRoleChanges = useCallback((role, diffs) => {
        setChanges((prev) => ({...prev, [role]: diffs}));
    }, []);

    const getRoleCount = useCallback((role) => {
        if (!summary) return {permitted: 0, total: 0};
        return computePermittedCount(summary, role, changes[role]);
    }, [summary, changes]);

    const isDirty = useMemo(
        () => Object.values(changes).some(hasDiffs),
        [changes],
    );

    const save = useCallback(async () => {
        const changesList = buildChangesList(changes);
        if (!changesList.length) return;
        try {
            await ProjectRequester.applyRoleVisibilityChanges(projectId, changesList, workspace);
            setChanges({});
            await loadSummary();
        } catch (error) {
            console.error('Failed to save role visibility changes:', error);
            throw error;
        }
    }, [changes, projectId, workspace, loadSummary]);

    const discard = useCallback(() => {
        setChanges({});
    }, []);

    return {
        summary, changes, getRoleCount, updateRoleChanges,
        isDirty, save, discard,
        projectId, projectName, workspace,
    };
};

export default useRoleAccessRules;
